import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { useAuth } from "../auth/AuthContext.js";
import { AccountMenu } from "../components/AccountMenu.js";
import { AddAccountDialog } from "../components/AddAccountDialog.js";
import { Composer, type ComposeSeed } from "../components/Composer.js";
import { DeviceSetup } from "../components/DeviceSetup.js";
import { FolderList } from "../components/FolderList.js";
import { Logo } from "../components/Logo.js";
import { MessageList } from "../components/MessageList.js";
import { MessageView } from "../components/MessageView.js";
import { Settings } from "../components/Settings.js";
import {
  IconFolderPlus,
  IconForward,
  IconInbox,
  IconJunk,
  IconMenu,
  IconPen,
  IconReply,
  IconReplyAll,
  IconStar,
  IconStarFilled,
  IconTrash,
} from "../components/icons.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { FolderDialog } from "../components/FolderDialog.js";
import { FolderMenu } from "../components/FolderMenu.js";
import { MoveToSheet } from "../components/MoveToSheet.js";
import { SelectionBar } from "../components/SelectionBar.js";
import { accountName } from "../lib/accounts.js";
import { ApiError, api } from "../lib/api.js";
import { beginDrag, setDragGhost, type DragState } from "../lib/dnd.js";
import { isDescendant, remapPath } from "../lib/folderTree.js";
import { onLive } from "../lib/live.js";
import { useFolderActions } from "../lib/useFolderActions.js";
import { useLongPress } from "../lib/useLongPress.js";
import { useMediaQuery } from "../lib/useMediaQuery.js";
import { useMessageSelection } from "../lib/useMessageSelection.js";
import type {
  AccountInfo,
  MailFolder,
  MessageDetail,
  MessageListPage,
  MessageSummary,
  UnreadByAccount,
} from "../lib/types.js";

const PAGE_SIZE = 50;

/**
 * Vangnet naast de live-verbinding: die meldt nieuwe post en wijzigingen via
 * de webmail, maar niet wat er via de telefoon of Outlook gebeurde (gelezen,
 * verplaatst). Elke ronde kost IMAP-verbindingen uit een emmer die de
 * gebruiker deelt met die apparaten; een minuut is vaak genoeg.
 */
const POLL_MS = 60_000;

/**
 * Meerdere seintjes vlak na elkaar (tien mails in één aflevering, een
 * selectie die gelezen wordt) leveren zo één verversing op in plaats van tien.
 */
const LIVE_DEBOUNCE_MS = 400;

/**
 * Onder 900px is er één kolom tegelijk zichtbaar; dit is welke. De mappen zijn
 * geen kolom meer maar een lade die over de lijst heen schuift (drawerOpen).
 */
type Pane = "list" | "message";

function errorTextOf(cause: unknown): string {
  return cause instanceof ApiError ? cause.message : "Er ging iets mis. Probeer het opnieuw.";
}

function defaultFolderPath(folders: MailFolder[]): string | null {
  const inbox = folders.find((folder) => folder.specialUse === "inbox" && folder.selectable);
  if (inbox !== undefined) return inbox.path;
  return folders.find((folder) => folder.selectable)?.path ?? null;
}

/**
 * `account` is de mailbox die getoond wordt. App geeft elke mailbox een eigen
 * Mail (key), zodat er bij wisselen nooit iets van de vorige blijft hangen.
 */
export function Mail({ account: active }: { account: AccountInfo }): ReactNode {
  const {
    accounts,
    remembered,
    notice: accountNotice,
    clearNotice: clearAccountNotice,
    logout,
    addAccount,
    removeAccount,
    switchAccount,
  } = useAuth();
  const multiple = accounts.length > 1;

  const [folders, setFolders] = useState<MailFolder[] | null>(null);
  const [foldersError, setFoldersError] = useState<string | null>(null);
  const [folderPath, setFolderPath] = useState<string | null>(null);

  const [page, setPage] = useState<MessageListPage | null>(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [reloadToken, setReloadToken] = useState(0);

  const [selectedUid, setSelectedUid] = useState<number | null>(null);
  const [detail, setDetail] = useState<MessageDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [pane, setPane] = useState<Pane>("list");

  /**
   * De mappenlade (mobiel). Op desktop is het mappenpaneel een vaste kolom en
   * doet deze status niets — alle lade-CSS leeft in het 900px-blok.
   */
  const [drawerOpen, setDrawerOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  // Zelfde breekpunt als styles.css; zie de kanttekening in useMediaQuery.ts.
  const isMobile = useMediaQuery("(max-width: 900px)");

  const [compose, setCompose] = useState<ComposeSeed | null>(null);
  /** Wat er in het zoekveld staat… */
  const [query, setQuery] = useState("");
  /**
   * …en waarop daadwerkelijk gezocht wordt. Gescheiden, zodat verversen (live
   * of de poller) de zoekresultaten opnieuw ophaalt in plaats van ze te
   * vervangen door de gewone lijst.
   */
  const [activeSearch, setActiveSearch] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<AccountInfo | null>(null);
  const [removing, setRemoving] = useState(false);

  /** Ongelezen post per mailbox, voor de wisselaar; alleen met meer dan één mailbox. */
  const [unread, setUnread] = useState<UnreadByAccount>({});

  /**
   * Verversingen die de gebruiker niet zelf deed (live-seintje, poller,
   * terugkeer in de app) laten de lijst niet even grijs worden: dat knippert
   * bij elke binnenkomende mail, en de nieuwe regel bovenaan zegt genoeg.
   */
  const quietReload = useRef(false);

  const [moveOpen, setMoveOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const [drag, setDrag] = useState<DragState | null>(null);
  /** Waar de sleep nu boven hangt; null = de rij "hoogste niveau". */
  const [dragOver, setDragOver] = useState<string | null | undefined>(undefined);

  const activeFolder = useMemo(
    () => folders?.find((folder) => folder.path === folderPath) ?? null,
    [folders, folderPath],
  );

  /**
   * De twee doelen van de spamknop. Beide kunnen ontbreken — een postvak
   * waarin de map Ongewenst ooit weggegooid is bestaat — en dan verdwijnt de
   * knop liever dan dat hij op een fout uitloopt.
   */
  const junkFolder = useMemo(
    () => folders?.find((folder) => folder.specialUse === "junk" && folder.selectable) ?? null,
    [folders],
  );
  const inboxFolder = useMemo(
    () => folders?.find((folder) => folder.specialUse === "inbox" && folder.selectable) ?? null,
    [folders],
  );

  /**
   * Welke kant de knop op wijst, of null als hij hier niet hoort. In Verzonden,
   * Concepten en de Prullenbak heeft "dit is spam" geen betekenis: die post is
   * nooit langs het filter gekomen, dus er valt ook niets van te leren.
   */
  const junkAction = useMemo<"spam" | "notSpam" | null>(() => {
    if (activeFolder === null) return null;
    if (activeFolder.specialUse === "junk") return inboxFolder === null ? null : "notSpam";
    if (activeFolder.specialUse === "sent") return null;
    if (activeFolder.specialUse === "drafts") return null;
    if (activeFolder.specialUse === "trash") return null;
    return junkFolder === null ? null : "spam";
  }, [activeFolder, inboxFolder, junkFolder]);

  /**
   * De uids in schermvolgorde: shift-klik moet het zichtbare bereik pakken,
   * niet het numerieke.
   */
  const orderedUids = useMemo(() => page?.messages.map((m) => m.uid) ?? [], [page]);
  const selection = useMessageSelection(orderedUids, folderPath);
  const longPress = useLongPress(selection.begin, isMobile);

  const [folderMenu, setFolderMenu] = useState<{ folder: MailFolder; anchor: HTMLElement } | null>(
    null,
  );

  const folderActions = useFolderActions({
    onFolders: setFolders,
    /*
     * Een hernoeming neemt de hele subboom mee. Staat de open map daar tussen,
     * dan wijst folderPath naar een pad dat niet meer bestaat — de lijst zou
     * dan met een 404 leeglopen terwijl er niets mis is.
     */
    onRenamed: (previousPath, nextPath, delimiter) => {
      setFolderPath((current) =>
        current === null ? current : remapPath(current, previousPath, nextPath, delimiter) ?? current,
      );
    },
    onRemoved: (removed) => {
      setFolderPath((current) => {
        if (current === null || !removed.includes(current)) return current;
        // De open map is weg. Op null zetten; het effect hieronder kiest dan
        // dezelfde standaardmap als bij het opstarten.
        setOffset(0);
        setSelectedUid(null);
        setDetail(null);
        setPane("list");
        return null;
      });
    },
    onNotice: setNotice,
  });

  const loadFolders = useCallback(async () => {
    try {
      const list = await api.folders();
      setFolders(list);
      setFoldersError(null);
      // Bij de eerste lading kiezen we zelf een map; daarna nooit meer, anders
      // springt de app onder de gebruiker weg tijdens het pollen.
      setFolderPath((current) => current ?? defaultFolderPath(list));
    } catch (cause) {
      setFoldersError(errorTextOf(cause));
    }
  }, []);

  useEffect(() => {
    void loadFolders();
  }, [loadFolders]);

  /**
   * Zonder open map er een kiezen. Geldt bij het opstarten én nadat de map die
   * openstond verwijderd is; in beide gevallen is "geen map" geen bruikbare
   * toestand.
   */
  useEffect(() => {
    if (folderPath !== null || folders === null) return;
    setFolderPath(defaultFolderPath(folders));
  }, [folders, folderPath]);

  useEffect(() => {
    if (folderPath === null) return;

    let cancelled = false;
    const quiet = quietReload.current;
    quietReload.current = false;
    if (!quiet) setPageLoading(true);
    const request =
      activeSearch === ""
        ? api.messages(folderPath, offset, PAGE_SIZE)
        : api.search(folderPath, activeSearch, "all", offset, PAGE_SIZE);
    request
      .then((result) => {
        if (cancelled) return;
        setPage(result);
        setPageError(null);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        // Een stille verversing die mislukt (even geen netwerk) laat de lijst
        // staan; een foutmelding over een lijst die er gewoon is helpt niemand.
        if (quiet) return;
        setPage(null);
        setPageError(errorTextOf(cause));
      })
      .finally(() => {
        if (!cancelled) setPageLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [folderPath, offset, reloadToken, activeSearch]);

  const markSeen = useCallback(async (folder: string, uid: number) => {
    try {
      await api.setFlags(folder, uid, { seen: true });
    } catch {
      // Lukt het niet, dan blijft het bericht ongelezen; dat is geen fout die
      // de lezer moet onderbreken.
      return;
    }
    setPage((current) =>
      current === null || current.folder !== folder
        ? current
        : {
            ...current,
            messages: current.messages.map((message) =>
              message.uid === uid ? { ...message, seen: true } : message,
            ),
          },
    );
    setFolders((current) =>
      current === null
        ? current
        : current.map((entry) =>
            entry.path === folder && entry.unseen > 0
              ? { ...entry, unseen: entry.unseen - 1 }
              : entry,
          ),
    );
  }, []);

  useEffect(() => {
    if (folderPath === null || selectedUid === null) {
      setDetail(null);
      setDetailError(null);
      return;
    }

    let cancelled = false;
    setDetail(null);
    setDetailError(null);
    setDetailLoading(true);
    api
      .message(folderPath, selectedUid)
      .then((result) => {
        if (cancelled) return;
        setDetail(result);
        if (!result.seen) void markSeen(folderPath, result.uid);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setDetailError(errorTextOf(cause));
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [folderPath, selectedUid, markSeen]);

  /** Lijst en mappen opnieuw ophalen zonder dat de gebruiker er iets van merkt. */
  const refreshQuietly = useCallback(() => {
    quietReload.current = true;
    setReloadToken((token) => token + 1);
    void loadFolders();
  }, [loadFolders]);

  const refreshUnread = useCallback(() => {
    if (accounts.length < 2) return;
    api
      .accountsUnread()
      .then(setUnread)
      .catch(() => undefined);
  }, [accounts.length]);

  useEffect(() => {
    refreshUnread();
  }, [refreshUnread]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      // Een tabblad op de achtergrond hoeft geen mailbox open te houden.
      if (document.hidden) return;
      refreshQuietly();
      refreshUnread();
    }, POLL_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [refreshQuietly, refreshUnread]);

  /**
   * Live: nieuwe post of een wijziging elders verschijnt meteen, en na
   * terugkeer in de app (telefoon uit de achtergrond) wordt alles één keer
   * vers opgehaald. Zonder dat laatste toonde een app op het beginscherm de
   * mailbox van uren geleden, tot je hem helemaal afsloot.
   */
  const composeOpen = useRef(false);
  composeOpen.current = compose !== null;

  useEffect(() => {
    let timer: number | undefined;
    let unreadTimer: number | undefined;
    const soon = (): void => {
      window.clearTimeout(timer);
      timer = window.setTimeout(refreshQuietly, LIVE_DEBOUNCE_MS);
    };
    const unreadSoon = (): void => {
      window.clearTimeout(unreadTimer);
      unreadTimer = window.setTimeout(refreshUnread, LIVE_DEBOUNCE_MS);
    };

    const stop = onLive((message) => {
      switch (message.type) {
        case "resume":
          soon();
          unreadSoon();
          return;
        case "open-account":
          // Een tik op een melding: naar die mailbox, behalve midden in een
          // half getypt bericht — dat zou verloren gaan.
          if (message.account !== active.id && !composeOpen.current) switchAccount(message.account);
          return;
        case "mail":
        case "changed":
          if (message.account === active.id) soon();
          unreadSoon();
          return;
      }
    });

    return () => {
      stop();
      window.clearTimeout(timer);
      window.clearTimeout(unreadTimer);
    };
  }, [active.id, refreshQuietly, refreshUnread, switchAccount]);

  // In de titel van het tabblad staat welke mailbox open is; met meerdere
  // tabbladen op een desktop is dat anders gokken.
  useEffect(() => {
    if (!multiple) return;
    const previous = document.title;
    document.title = `${accountName(active.address)} — RULIDO WEBMAIL`;
    return () => {
      document.title = previous;
    };
  }, [active.address, multiple]);

  // Escape sluit de lade en geeft de focus terug aan de hamburger; zonder dat
  // belandt een toetsenbordgebruiker in het niets.
  useEffect(() => {
    if (!drawerOpen) return;
    drawerRef.current?.focus();
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        setDrawerOpen(false);
        hamburgerRef.current?.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  // Venster verbreden met een open lade mag geen zwevende backdrop achterlaten.
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  function openFolder(folder: MailFolder): void {
    if (folder.path === folderPath) {
      setDrawerOpen(false);
      setPane("list");
      return;
    }
    setFolderPath(folder.path);
    setOffset(0);
    // Een zoekopdracht hoort bij de map waarin hij gedaan werd.
    setQuery("");
    setActiveSearch("");
    // De oude lijst hoort niet onder de nieuwe mapnaam te blijven staan.
    setPage(null);
    setPageError(null);
    setSelectedUid(null);
    setDrawerOpen(false);
    setPane("list");
  }

  function openMessage(message: MessageSummary): void {
    setSelectedUid(message.uid);
    setPane("message");
  }

  const showRecipients =
    activeFolder?.specialUse === "sent" || activeFolder?.specialUse === "drafts";

  /** Lijst en mappen opnieuw laden na een actie die iets veranderd heeft. */
  function refresh(): void {
    setReloadToken((token) => token + 1);
    void loadFolders();
  }

  /**
   * Haalt verwerkte berichten meteen uit de lijst op het scherm. Puur om de
   * flits te vermijden: `refresh()` erachteraan haalt de waarheid op.
   */
  function pruneFromPage(uids: readonly number[]): void {
    const weg = new Set(uids);
    setPage((current) =>
      current === null
        ? current
        : {
            ...current,
            messages: current.messages.filter((m) => !weg.has(m.uid)),
            total: Math.max(0, current.total - uids.length),
          },
    );
    if (selectedUid !== null && weg.has(selectedUid)) {
      setSelectedUid(null);
      setDetail(null);
      setPane("list");
    }
    selection.drop(uids);
  }

  /** De berichten waar een bulkactie op werkt: de selectie, in schermvolgorde. */
  function selectedUids(): number[] {
    return orderedUids.filter((uid) => selection.selected.has(uid));
  }

  /**
   * `noticeFor` overschrijft de melding. Verplaatsen naar Ongewenst is voor de
   * server hetzelfde werk, maar voor de gebruiker iets anders dan opbergen —
   * dat verschil zit alleen in de tekst.
   */
  async function moveUids(
    from: string,
    uids: number[],
    target: MailFolder,
    noticeFor?: (count: number) => string,
  ): Promise<void> {
    if (uids.length === 0 || target.path === from) return;

    setBusy(true);
    try {
      await api.move(from, uids, target.path);
      pruneFromPage(uids);
      selection.clear();
      setNotice(
        noticeFor !== undefined
          ? noticeFor(uids.length)
          : uids.length === 1
            ? `Bericht verplaatst naar ${target.name}.`
            : `${uids.length} berichten verplaatst naar ${target.name}.`,
      );
      refresh();
    } catch (cause) {
      setNotice(errorTextOf(cause));
    } finally {
      setBusy(false);
      setMoveOpen(false);
    }
  }

  async function moveSelection(target: MailFolder): Promise<void> {
    if (folderPath === null) return;
    await moveUids(folderPath, selectedUids(), target);
  }

  /**
   * Naar Ongewenst, of er juist uit.
   *
   * Verplaatsen is hier maar de helft van wat er gebeurt: de mailserver ziet
   * de verplaatsing langskomen en biedt het bericht aan het spamfilter aan als
   * voorbeeld van spam, respectievelijk van goede post. Dat leren zit in
   * Dovecot en niet achter deze knop — precies daarom werkt het net zo goed
   * als de gebruiker het bericht op zijn telefoon wegsleept.
   */
  async function reportJunk(uids: number[]): Promise<void> {
    if (folderPath === null || junkAction === null) return;

    const target = junkAction === "spam" ? junkFolder : inboxFolder;
    if (target === null) return;

    await moveUids(folderPath, uids, target, (count) => {
      const wat = count === 1 ? "Bericht" : `${count} berichten`;
      return junkAction === "spam"
        ? `${wat} als ongewenst gemarkeerd. Het filter leert hiervan.`
        : `${wat} teruggezet in ${target.name}. Het filter leert hiervan.`;
    });
  }

  // ── Slepen ────────────────────────────────────────────────────────────────

  function startMessageDrag(message: MessageSummary, event: React.DragEvent): void {
    if (folderPath === null) return;
    // Een geselecteerd bericht neemt de hele selectie mee; een ander bericht
    // gaat alleen. Zo werken Verkenner en Outlook ook.
    const uids = selection.has(message.uid) ? selectedUids() : [message.uid];
    beginDrag(event, { kind: "messages", folder: folderPath, uids });
    if (uids.length > 1) setDragGhost(event, `${uids.length} berichten`);
    setDrag({ kind: "messages", folder: folderPath, uids });
  }

  function endDrag(): void {
    setDrag(null);
    setDragOver(undefined);
  }

  function dropOnFolder(target: MailFolder): void {
    const current = drag;
    endDrag();
    if (current?.kind !== "messages") return;
    void moveUids(current.folder, current.uids, target);
  }

  function startFolderDrag(folder: MailFolder, event: React.DragEvent): void {
    beginDrag(event, { kind: "folder", path: folder.path });
    setDrag({ kind: "folder", path: folder.path });
  }

  function dropFolderOn(target: MailFolder | null): void {
    const current = drag;
    endDrag();
    if (current?.kind !== "folder") return;

    const source = folders?.find((f) => f.path === current.path);
    if (source === undefined) return;
    // De lijst weigert dit al bij dragover, maar een drop kan ook via een
    // verlopen sleep binnenkomen; de server weigert het hoe dan ook.
    if (target !== null && (target.path === source.path || isDescendant(source, target.path))) return;

    folderActions.nest(source, target);
  }

  async function removeSelection(): Promise<void> {
    if (folderPath === null) return;
    const uids = selectedUids();
    if (uids.length === 0) return;

    setBusy(true);
    try {
      const result = await api.remove(folderPath, uids);
      pruneFromPage(uids);
      selection.clear();
      setNotice(
        result.mode === "trash"
          ? `${result.count === 1 ? "Bericht" : `${result.count} berichten`} naar de Prullenbak verplaatst.`
          : `${result.count === 1 ? "Bericht" : `${result.count} berichten`} definitief verwijderd.`,
      );
      refresh();
    } catch (cause) {
      setNotice(errorTextOf(cause));
    } finally {
      setBusy(false);
    }
  }

  async function markSelectionRead(): Promise<void> {
    if (folderPath === null) return;
    const uids = selectedUids();
    if (uids.length === 0) return;

    setBusy(true);
    try {
      await api.setFlagsBulk(folderPath, uids, { seen: true });
      const gelezen = new Set(uids);
      setPage((current) =>
        current === null
          ? current
          : {
              ...current,
              messages: current.messages.map((m) =>
                gelezen.has(m.uid) ? { ...m, seen: true } : m,
              ),
            },
      );
      selection.clear();
      refresh();
    } catch (cause) {
      setNotice(errorTextOf(cause));
    } finally {
      setBusy(false);
    }
  }

  async function removeCurrent(): Promise<void> {
    if (folderPath === null || selectedUid === null) return;
    try {
      const result = await api.remove(folderPath, [selectedUid]);
      setNotice(
        result.mode === "trash"
          ? "Bericht naar de Prullenbak verplaatst."
          : "Bericht definitief verwijderd.",
      );
      setSelectedUid(null);
      setDetail(null);
      setPane("list");
      refresh();
    } catch (cause) {
      setNotice(errorTextOf(cause));
    }
  }

  async function toggleFlag(): Promise<void> {
    if (folderPath === null || detail === null) return;
    const next = !detail.flagged;
    setDetail({ ...detail, flagged: next });
    try {
      await api.setFlags(folderPath, detail.uid, { flagged: next });
      refresh();
    } catch (cause) {
      setDetail({ ...detail, flagged: !next });
      setNotice(errorTextOf(cause));
    }
  }

  /** Zoeken loopt via het lijst-effect; zo blijft een verversing binnen de zoekopdracht. */
  function runSearch(term: string): void {
    if (folderPath === null) return;
    setQuery(term);
    setOffset(0);
    setActiveSearch(term.trim());
    if (term.trim() === "") void loadFolders();
  }

  async function confirmRemoval(): Promise<void> {
    if (pendingRemoval === null) return;
    setRemoving(true);
    try {
      await removeAccount(pendingRemoval.id);
    } catch (cause) {
      setNotice(errorTextOf(cause));
    } finally {
      setRemoving(false);
      setPendingRemoval(null);
    }
  }

  /** De teller van de open mailbox komt uit de mappenlijst, die is het actueelst. */
  const unreadView = useMemo<UnreadByAccount>(
    () => ({ ...unread, [active.id]: inboxFolder?.unseen ?? unread[active.id] ?? null }),
    [unread, active.id, inboxFolder],
  );

  const shownNotice = notice ?? accountNotice;

  return (
    <div className="app">
      <header className="topbar">
        <div className="topbar-left">
          <button
            ref={hamburgerRef}
            type="button"
            className="hamburger"
            aria-label="Mappen"
            aria-expanded={drawerOpen}
            aria-controls="mappen-lade"
            onClick={() => setDrawerOpen(true)}
          >
            <IconMenu size={22} />
          </button>
          <Logo className="logo--topbar" />
        </div>
        <AccountMenu
          accounts={accounts}
          active={active}
          unread={unreadView}
          onSwitch={switchAccount}
          onAddAccount={() => setAddAccountOpen(true)}
          onRemoveAccount={setPendingRemoval}
          onDeviceSetup={() => setSetupOpen(true)}
          onSettings={() => setSettingsOpen(true)}
          onLogout={() => void logout()}
        />
      </header>

      <div className="layout" data-pane={pane}>
        {drawerOpen && (
          <button
            type="button"
            className="drawer-backdrop"
            aria-label="Mappen sluiten"
            onClick={() => setDrawerOpen(false)}
          />
        )}

        <section
          id="mappen-lade"
          ref={drawerRef}
          tabIndex={-1}
          className={`pane pane--folders${drawerOpen ? " is-open" : ""}`}
          aria-label="Mappen"
          role={isMobile ? "dialog" : undefined}
          aria-modal={isMobile && drawerOpen ? true : undefined}
        >
          <div className="pane-head">
            <button
              type="button"
              className="compose-button"
              onClick={() => {
                setDrawerOpen(false);
                setCompose({ mode: "new" });
              }}
            >
              <IconPen size={18} />
              Nieuw bericht
            </button>
          </div>
          <div className="pane-body">
            <button
              type="button"
              className="button button--ghost new-folder-button"
              onClick={() => folderActions.openCreate(null)}
            >
              <IconFolderPlus />
              Nieuwe map
            </button>
            <FolderList
              folders={folders}
              activePath={folderPath}
              error={foldersError}
              onSelect={openFolder}
              dnd={{
                enabled: !isMobile,
                kind: drag?.kind ?? null,
                sourcePath: drag === null ? null : drag.kind === "messages" ? drag.folder : drag.path,
                overPath: dragOver,
                onOver: (target) => setDragOver(target?.path ?? null),
                onLeave: () => setDragOver(undefined),
                onDropMessages: dropOnFolder,
                onDropFolder: dropFolderOn,
                onFolderDragStart: startFolderDrag,
                onDragEnd: endDrag,
              }}
              onFolderMenu={(folder, anchor) => setFolderMenu({ folder, anchor })}
            />
          </div>
        </section>

        <section className="pane pane--list" aria-label="Berichten">
          <div className="pane-head">
            <h2 className="pane-title">{activeFolder?.name ?? "Berichten"}</h2>
            <input
              type="search"
              className="search-input"
              value={query}
              placeholder="Zoek op afzender of onderwerp"
              aria-label="Zoeken"
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") runSearch(query);
                if (e.key === "Escape") runSearch("");
              }}
            />
          </div>
          {selection.mode && (
            <SelectionBar
              count={selection.selected.size}
              busy={busy}
              junk={junkAction}
              onMove={() => setMoveOpen(true)}
              onJunk={() => void reportJunk(selectedUids())}
              onDelete={() => void removeSelection()}
              onMarkRead={() => void markSelectionRead()}
              onSelectAll={selection.selectAll}
              onCancel={selection.clear}
            />
          )}
          <div className={`pane-body${selection.mode ? " has-selection-bar" : ""}`}>
            <MessageList
              page={page}
              loading={pageLoading}
              error={pageError}
              selectedUid={selectedUid}
              showRecipients={showRecipients}
              onOpen={openMessage}
              onPrevious={() => setOffset((current) => Math.max(0, current - PAGE_SIZE))}
              onNext={() => setOffset((current) => current + PAGE_SIZE)}
              selection={selection.selected}
              selectionMode={selection.mode}
              onSelect={selection.select}
              longPress={longPress}
              dragEnabled={!isMobile}
              onDragStart={startMessageDrag}
              onDragEnd={endDrag}
            />
          </div>
        </section>

        <section className="pane pane--message" aria-label="Bericht">
          <div className="pane-head">
            <button type="button" className="back" onClick={() => setPane("list")}>
              Berichten
            </button>
            <h2 className="pane-title">Bericht</h2>
            {detail !== null && folderPath !== null && (
              <div className="message-actions">
                <button type="button" className="button button--ghost"
                  onClick={() => setCompose({ mode: "reply", source: { folder: folderPath, message: detail } })}>
                  <IconReply />
                  <span className="button-label">Beantwoorden</span>
                </button>
                <button type="button" className="button button--ghost"
                  onClick={() => setCompose({ mode: "replyAll", source: { folder: folderPath, message: detail } })}>
                  <IconReplyAll />
                  <span className="button-label">Allen</span>
                </button>
                <button type="button" className="button button--ghost"
                  onClick={() => setCompose({ mode: "forward", source: { folder: folderPath, message: detail } })}>
                  <IconForward />
                  <span className="button-label">Doorsturen</span>
                </button>
                <button type="button" className="button button--ghost"
                  onClick={() => void toggleFlag()} aria-pressed={detail.flagged}>
                  {detail.flagged ? <IconStarFilled /> : <IconStar />}
                  <span className="button-label">{detail.flagged ? "Ster weg" : "Ster"}</span>
                </button>
                {junkAction !== null && (
                  <button type="button" className="button button--ghost"
                    onClick={() => void reportJunk([detail.uid])}>
                    {junkAction === "spam" ? <IconJunk /> : <IconInbox />}
                    <span className="button-label">
                      {junkAction === "spam" ? "Ongewenst" : "Geen spam"}
                    </span>
                  </button>
                )}
                <button type="button" className="button button--danger" onClick={() => void removeCurrent()}>
                  <IconTrash />
                  <span className="button-label">Verwijderen</span>
                </button>
              </div>
            )}
          </div>
          <div className="pane-body">
            <MessageView
              // Nieuw bericht is een nieuwe component: de keuze om externe
              // afbeeldingen te tonen mag niet meeliften naar het volgende.
              key={`${folderPath ?? ""}:${selectedUid ?? ""}`}
              message={detail}
              loading={detailLoading}
              error={detailError}
            />
          </div>
        </section>
      </div>

      {/* De zwevende knop wijkt voor de selectiebalk; die staat op dezelfde plek. */}
      {pane !== "message" && !selection.mode && (
        <button
          type="button"
          className="fab"
          aria-label="Nieuw bericht"
          onClick={() => setCompose({ mode: "new" })}
        >
          <IconPen size={24} />
        </button>
      )}

      {moveOpen && folders !== null && folderPath !== null && (
        <MoveToSheet
          title="Verplaatsen naar…"
          folders={folders}
          disabledPaths={new Set([folderPath])}
          onCreateFolder={() => {
            setMoveOpen(false);
            folderActions.openCreate(null);
          }}
          onPick={(target) => {
            if (target !== null) void moveSelection(target);
          }}
          onClose={() => setMoveOpen(false)}
        />
      )}

      {folderMenu !== null && (
        <FolderMenu
          folder={folderMenu.folder}
          anchor={folderMenu.anchor}
          onCreateChild={() => {
            folderActions.openCreate(folderMenu.folder);
            setFolderMenu(null);
          }}
          onRename={() => {
            folderActions.openRename(folderMenu.folder);
            setFolderMenu(null);
          }}
          onDelete={() => {
            folderActions.remove(folderMenu.folder);
            setFolderMenu(null);
          }}
          onClose={() => setFolderMenu(null)}
        />
      )}

      {folderActions.dialog !== null && (
        <FolderDialog
          state={folderActions.dialog}
          busy={folderActions.busy}
          error={folderActions.dialogError}
          onSubmit={folderActions.submitDialog}
          onClose={folderActions.closeDialog}
        />
      )}

      {folderActions.pendingDelete !== null && (
        <ConfirmDialog
          title={`"${folderActions.pendingDelete.folder.name}" verwijderen?`}
          message={folderActions.pendingDelete.message}
          confirmLabel="Definitief verwijderen"
          busy={folderActions.busy}
          onConfirm={folderActions.confirmRemove}
          onClose={folderActions.cancelRemove}
        />
      )}

      {shownNotice !== null && (
        <div
          className="toast"
          role="status"
          onClick={() => {
            setNotice(null);
            clearAccountNotice();
          }}
        >
          {shownNotice}
        </div>
      )}

      {setupOpen && (
        <DeviceSetup address={active.address} onClose={() => setSetupOpen(false)} />
      )}

      {settingsOpen && <Settings onClose={() => setSettingsOpen(false)} />}

      {addAccountOpen && (
        <AddAccountDialog
          remembered={remembered}
          onAdd={addAccount}
          onClose={() => setAddAccountOpen(false)}
        />
      )}

      {pendingRemoval !== null && (
        <ConfirmDialog
          title={`${accountName(pendingRemoval.address)} afmelden?`}
          message={`${pendingRemoval.address} verdwijnt uit deze app. Uw post blijft gewoon op de server staan; u kunt de mailbox later opnieuw toevoegen met het wachtwoord.`}
          confirmLabel="Afmelden"
          busy={removing}
          onConfirm={() => void confirmRemoval()}
          onClose={() => setPendingRemoval(null)}
        />
      )}

      {compose !== null && (
        <Composer
          seed={compose}
          accounts={accounts}
          from={active.id}
          onClose={() => setCompose(null)}
          onSent={() => {
            setCompose(null);
            setNotice("Bericht verzonden.");
            refresh();
          }}
        />
      )}
    </div>
  );
}
